<template>
  <div
    :class="{
      'refer-a-friend-box my-30px rounded-5px border border-www-color-primary-light bg-www-color-white px-25px py-5': true,
    }"
  >
    <div
      v-if="!isUserInit"
      :class="{
        'flex items-center justify-center text-center': true,
      }"
    >
      <div class="spinner"></div>
    </div>
    <div v-else-if="user && hasReferralCode">
      <p :class="{ 'mb-10px text-lg font-bold': true }">{{ title }}</p>
      <p :class="{ 'mb-10px text-base font-semibold': true }">{{ subtitle }}</p>
      <div :class="{ 'flex w-full flex-wrap items-center gap-10px': true }">
        <input
          data-testid="referAFriendField"
          :class="{
            'rounded-5px border border-www-color-neutral-medium-light bg-www-color-neutral-off-white px-18px py-3': true,
            'flex-grow': true,
            'text-sm text-www-color-neutral-medium-dark placeholder:text-www-color-neutral-medium-dark': true,
          }"
          :placeholder="refUrl"
          :value="refUrl"
        />
        <button
          :class="{
            'rounded-5px bg-www-color-primary-lighter px-2 py-3 text-sm text-www-color-white': true,
            'disabled cursor-not-allowed opacity-50': !refUrl || referralCountLeft <= 0,
          }"
          @click="handleCopy(refUrl)"
        >
          <PtcIcon name="ptc-externallink" /> {{ buttonText }}
        </button>
      </div>
      <p
        :class="{
          'referral-available mt-10px text-sm font-extrabold text-www-color-primary-lighter': true,
        }"
      >
        {{ referralCountLeft }} available
      </p>
    </div>

    <div
      v-else
      :class="{ 'text-center': true }"
    >
      <p :class="{ 'text-lg font-bold': true }">{{ title }}</p>
      <p
        v-if="subtitle"
        :class="{ 'my-10px text-base font-semibold': true }"
      >
        {{ subtitle }}
      </p>
      <Button
        v-if="buttonText && buttonLink"
        :button-props="{
          _type: 'button',
          text: buttonText,
          style: 'primary',
          link: [
            {
              _key: 'link',
              _type: 'externalLink',
              url: buttonLink,
            },
          ],
        }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import PtcIcon from '~/components/Atmos/PtcIcon/PtcIcon.vue';
import Button from '~/components/Atmos/Button/Button.vue';
import type { ReferAFriendBox } from '~/sanity/types';
const props = defineProps<ReferAFriendBox & { lpPathname?: string }>();

const { user, isUserInit } = useCrossSession();
const { isPaid } = usePersonalization();
const isPaidUser = computed(() => user.value && isPaid.value);
const hasReferralCode = computed(() => {
  if (!user || !isPaidUser.value) return false;
  if (!user.value?.refCodeData) return false;

  const { refCodeData } = user.value;
  return !refCodeData.isEmpty;
});

const baseUrl = useRequestURL().origin;

const { utmParams } = useUtmParams();
const refUrl = computed(() => {
  if (!user || !isPaidUser.value || !user.value?.refCodeData) {
    return '';
  }
  const { refCodeData } = user.value;
  if (!refCodeData || refCodeData.isEmpty || !refCodeData.defaultCode) return '';
  const refCode = refCodeData.defaultCode;

  const referralUrl = new URL(`${props.lpPathname}`, `${baseUrl}`);
  referralUrl.searchParams.set('refCode', refCode);
  for (const [key, value] of Object.entries(utmParams.value)) {
    referralUrl.searchParams.set(key, value);
  }

  return referralUrl.href;
});

const handleCopy = async (content: string) => {
  try {
    await navigator.clipboard.writeText(content);
  } catch (err) {
    throw new Error(`Failed to copy: ${err}`);
  }
};

const referralCountLeft = computed(() => {
  if (!user || !isPaidUser.value) return 0;
  if (!user.value?.refCodeData) return 0;

  const { refCodeData } = user.value;
  if (!refCodeData || refCodeData.isEmpty || typeof refCodeData.defaultCodeTimesUsed !== 'number') {
    return 0;
  }
  const maxTimesCanBeUsed = 6; // TODO make this dynamic
  const countLeft = maxTimesCanBeUsed - refCodeData.defaultCodeTimesUsed;
  return countLeft;
});
</script>

<style scoped>
.spinner {
  border: 4px solid var(--www-color-primary-lighter);
  border-left-color: var(--www-color-primary-pale);
  border-radius: 50%;
  width: 45px;
  height: 45px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
